import { useEffect, useState, useMemo } from "react";
import { Inbox, Search, Mail, Phone, Trash2, Check, Clock, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import useSeo from "@/hooks/useSeo";

export default function AdminLeads() {
  useSeo({ title: "Leads — RSR Admin" });
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all"); // all | unread | read
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/leads");
      setLeads(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return leads.filter(l => {
      if (filter === "unread" && l.read) return false;
      if (filter === "read" && !l.read) return false;
      if (!q.trim()) return true;
      const haystack = `${l.name} ${l.email} ${l.phone || ""} ${l.service || ""} ${l.message}`.toLowerCase();
      return haystack.includes(q.toLowerCase());
    });
  }, [leads, q, filter]);

  const unreadCount = leads.filter(l => !l.read).length;

  const markRead = async (id, read) => {
    try {
      await api.patch(`/admin/leads/${id}/read?read=${read}`);
      setLeads(ls => ls.map(l => l.id === id ? { ...l, read } : l));
      if (selected?.id === id) setSelected({ ...selected, read });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const removeLead = async (id) => {
    if (!confirm("Delete this lead? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/leads/${id}`);
      setLeads(ls => ls.filter(l => l.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success("Lead deleted.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="px-5 sm:px-8 lg:px-12 py-8 lg:py-12" data-testid="admin-leads">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <p className="overline">Inbox</p>
          <h1 className="mt-2 font-display text-4xl font-extrabold tracking-tighter">Leads <span className="text-muted-foreground font-medium text-2xl tabular-nums">({leads.length})</span></h1>
          {unreadCount > 0 && <p className="mt-1 text-sm text-accent font-semibold">{unreadCount} unread</p>}
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              data-testid="leads-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, email, service…"
              className="w-72 max-w-full bg-background border border-border pl-10 pr-3 py-2.5 text-sm focus:border-accent focus:outline-none"
            />
          </div>
          <div className="flex border border-border">
            {["all", "unread", "read"].map(f => (
              <button
                key={f}
                data-testid={`leads-filter-${f}`}
                onClick={() => setFilter(f)}
                className={`px-3 py-2.5 text-xs uppercase tracking-[0.18em] font-semibold ${filter === f ? "bg-foreground text-background" : "text-foreground/70 hover:text-foreground"}`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="py-20 text-center text-muted-foreground text-sm">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="border border-border bg-card p-16 text-center">
          <Inbox className="w-10 h-10 text-muted-foreground mx-auto mb-4" strokeWidth={1.5} />
          <h3 className="font-display text-2xl font-extrabold tracking-tight">No leads here yet.</h3>
          <p className="mt-2 text-sm text-muted-foreground">When someone fills out the contact form, they'll appear in this list.</p>
        </div>
      ) : (
        <div className="grid grid-cols-12 gap-6">
          <div className={`${selected ? "hidden lg:block col-span-12 lg:col-span-5" : "col-span-12"}`}>
            <div className="border border-border bg-card divide-y divide-border">
              {filtered.map(l => (
                <button
                  key={l.id}
                  onClick={() => { setSelected(l); if (!l.read) markRead(l.id, true); }}
                  data-testid={`lead-row-${l.id}`}
                  className={`w-full text-left p-5 flex items-start gap-4 hover:bg-secondary transition-colors ${selected?.id === l.id ? "bg-secondary" : ""}`}
                >
                  <div className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${l.read ? "bg-border" : "bg-accent"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-3">
                      <div className="font-semibold text-sm truncate">{l.name}</div>
                      <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground whitespace-nowrap">{new Date(l.created_at).toLocaleDateString("en-AU", { day: "2-digit", month: "short" })}</div>
                    </div>
                    <div className="text-xs text-muted-foreground truncate mt-0.5">{l.service || "—"} · {l.email}</div>
                    <div className="text-xs text-foreground/70 truncate mt-1.5">{l.message}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {selected && (
            <div className="col-span-12 lg:col-span-7" data-testid="lead-detail">
              <div className="border border-border bg-card">
                <div className="p-6 border-b border-border flex items-start justify-between gap-4">
                  <div>
                    <p className="overline">Lead · {selected.id.slice(0, 8).toUpperCase()}</p>
                    <h2 className="mt-2 font-display text-2xl font-extrabold tracking-tight">{selected.name}</h2>
                    <p className="mt-1 text-sm text-muted-foreground">{new Date(selected.created_at).toLocaleString("en-AU")}</p>
                  </div>
                  <button onClick={() => setSelected(null)} className="text-foreground/50 hover:text-foreground"><X className="w-5 h-5" /></button>
                </div>
                <div className="p-6 space-y-3 text-sm">
                  <Row label="Service" value={<span className="font-semibold">{selected.service || "—"}</span>} />
                  <Row label="Email" value={<a href={`mailto:${selected.email}`} className="text-accent hover:underline">{selected.email}</a>} />
                  <Row label="Phone" value={selected.phone ? <a href={`tel:${selected.phone}`} className="text-accent hover:underline">{selected.phone}</a> : "—"} />
                  <Row label="Customer auto-reply" value={selected.customer_email_sent ? <span className="text-green-600 inline-flex items-center gap-1"><Check className="w-3.5 h-3.5" /> Sent</span> : <span className="text-muted-foreground inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Not sent</span>} />
                  <Row label="Admin notification" value={selected.admin_email_sent ? <span className="text-green-600 inline-flex items-center gap-1"><Check className="w-3.5 h-3.5" /> Sent</span> : <span className="text-muted-foreground inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Not sent</span>} />
                </div>
                <div className="p-6 border-t border-border">
                  <div className="overline mb-2">Message</div>
                  <div className="text-sm leading-relaxed whitespace-pre-wrap text-foreground/90 bg-background border border-border p-4">{selected.message}</div>
                </div>
                <div className="p-6 border-t border-border flex flex-wrap gap-2">
                  <a href={`mailto:${selected.email}?subject=Re%3A%20Your%20enquiry%20%E2%80%94%20RSR%20Australia`} className="inline-flex items-center gap-2 bg-accent text-white px-5 py-2.5 text-sm font-semibold hover:bg-accent/90"><Mail className="w-4 h-4" /> Reply by email</a>
                  {selected.phone && <a href={`tel:${selected.phone}`} className="inline-flex items-center gap-2 border border-border hover:border-foreground/40 px-5 py-2.5 text-sm font-semibold"><Phone className="w-4 h-4" /> Call</a>}
                  <button onClick={() => markRead(selected.id, !selected.read)} className="inline-flex items-center gap-2 border border-border hover:border-foreground/40 px-5 py-2.5 text-sm font-semibold">
                    {selected.read ? "Mark unread" : "Mark read"}
                  </button>
                  <button onClick={() => removeLead(selected.id)} className="inline-flex items-center gap-2 border border-destructive/40 text-destructive hover:bg-destructive/5 px-5 py-2.5 text-sm font-semibold ml-auto"><Trash2 className="w-4 h-4" /> Delete</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="grid grid-cols-3 gap-4">
      <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground pt-0.5">{label}</div>
      <div className="col-span-2 text-foreground">{value}</div>
    </div>
  );
}
